import { invoiceItemCreatorTabOrder } from "../../Business/Documents/Invoice/InvoiceCreator/InvoiceItemCreator/InvoiceItemCreatorTabOrder";
import { UISettings } from "./interfaces";

export const DEFAULT_UI_SETTINGS: UISettings = {
    alerts: {
        position: 'top',
        useAnimation: true,
        opacity: 1.0,
        duration: 5000
    },
    documents: {
        invoiceItemCreator: invoiceItemCreatorTabOrder
    },
    tabBehavior: {
        cycle: true,
        rememberLastField: false
    }
};

export const UI_SETTINGS_KEY = 'uiSettings';

export class UISettingsManager {
    // Получение текущих настроек из localStorage
    static getSettings(): UISettings {
        try {
            const saved = localStorage.getItem(UI_SETTINGS_KEY);
            if (saved) {
                const parsed = JSON.parse(saved) as Partial<UISettings>;
                // Глубокое слияние с дефолтными настройками
                return this.deepMerge(DEFAULT_UI_SETTINGS, parsed);
            }
        } catch (error) {
            console.error("Ошибка при загрузке UI настроек", error);
        }
        return DEFAULT_UI_SETTINGS;
    }

    // Глубокое слияние объектов
    private static deepMerge<T extends object>(target: T, source: Partial<T>): T {
        const result = { ...target };
        
        for (const key in source) {
            if (source[key] !== undefined) {
                if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
                    result[key] = this.deepMerge(result[key] as object, source[key] as Partial<object>) as T[Extract<keyof T, string>];
                } else {
                    result[key] = source[key] as T[Extract<keyof T, string>];
                }
            }
        }
        
        return result;
    }

    // Сохранение настроек в localStorage и оповещение приложения об изменениях
    static saveSettings(settings: UISettings): void {
        try {
            localStorage.setItem(UI_SETTINGS_KEY, JSON.stringify(settings));
            window.dispatchEvent(new CustomEvent('uiSettingsChanged', { 
                detail: settings 
            }));
        } catch (error) {
            console.error("Ошибка при сохранении UI настроек", error);
        }
    }

    // Обновление всех или нескольких разделов настроек
    static updateSettings(updates: Partial<UISettings>): UISettings {
        const current = this.getSettings();
        const updated = this.deepMerge(current, updates);
        this.saveSettings(updated);
        return updated;
    }

    // Обновление конкретного раздела настроек
    static updateSection<T extends keyof UISettings>(
        section: T,
        updates: Partial<UISettings[T]>
    ): UISettings {
        const current = this.getSettings();
        const sectionUpdates = { ...current[section], ...updates } as UISettings[T];
        const updated = { ...current, [section]: sectionUpdates };
        this.saveSettings(updated);
        return updated;
    }

    // Полный сброс всех настроек к значениям по умолчанию
    static resetToDefault(): UISettings {
        this.saveSettings(DEFAULT_UI_SETTINGS);
        return DEFAULT_UI_SETTINGS;
    }

    // Сброс конкретного раздела настроек к значениям по умолчанию
    static resetSection<T extends keyof UISettings>(section: T): UISettings {
        const current = this.getSettings();
        const updated = {
            ...current,
            [section]: DEFAULT_UI_SETTINGS[section]
        };
        this.saveSettings(updated);
        return updated;
    }
}